/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* ============================================================================
 * Infinite Options のギフトオプションをカート追加時に確実に反映する
 *
 * 背景: Focal v13 の product-form は submit を即 preventDefault し
 * FormData(this) で /cart/add.js へ送信するため、フォーム要素の外にある
 * Infinite Options のフィールド（properties[...]）が送信に含まれない。
 * v13公開（2026-08-17）以降、熨斗・メッセージカード・云われカード等の
 * ギフトオプションが注文に入らなくなっていた。
 *
 * 対策: submit イベントのキャプチャ段階（product-form のハンドラより先に
 * 実行される）で、Infinite Options コンテナ内の有効なフィールドを
 * hidden input としてフォーム内に複製する。
 * ========================================================================== */
document.addEventListener('submit', function (event) {
  var form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  var action = form.getAttribute('action') || '';
  if (action.indexOf('/cart/add') === -1) return;

  var io = document.getElementById('infiniteoptions-container');
  if (!io || form.contains(io)) return;

  // 前回複製した分を消してから作り直す（値の変更・二重送信対策）
  form.querySelectorAll('[data-io-mirror]').forEach(function (n) { n.remove(); });

  io.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach(function (el) {
    if (el.disabled) return;
    if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
    var hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = el.name;
    hidden.value = el.value;
    hidden.setAttribute('data-io-mirror', '');
    form.appendChild(hidden);
  });
}, true);

/* ============================================================================
 * ebinoya独自: 日替わりセール表示の当日判定（2026-09-03）
 *
 * 背景: Shopifyはレンダリング済みページをしばらくキャッシュして配信するため、
 * Liquidの 'now' で「今日の対象商品」を判定すると、0時の切り替えがキャッシュの
 * 寿命ぶん遅れる（実測: 同一URLで約30秒後も同じレンダリング時刻が返った）。
 *
 * 対策: テーマ側はセール日ごとの表示要素を display:none で出力し、
 * ここでブラウザ側のJST日付と突き合わせて当日分だけ表示する。
 *  - [data-ebinoya-sale-date="YYYY-MM-DD"] … その日だけ表示する要素（ラベル・価格・商品ページの枠）
 *  - [data-ebinoya-sale-dates="d1,d2"]     … 商品カードの price-list。当日が含まれる日は
 *                                            通常価格（data属性の無い .price）を隠す
 * 対象: snippets/ebinoya-daily-sale.liquid / snippets/product-item.liquid
 * ========================================================================== */
(function () {
  function todayJST() {
    try {
      return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch (e) {
      return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
    }
  }

  function apply() {
    var today = todayJST();
    document.querySelectorAll('[data-ebinoya-sale-date]').forEach(function (el) {
      el.style.display = (el.getAttribute('data-ebinoya-sale-date') === today) ? '' : 'none';
    });
    document.querySelectorAll('[data-ebinoya-sale-dates]').forEach(function (list) {
      var on = list.getAttribute('data-ebinoya-sale-dates').split(',').indexOf(today) !== -1;
      Array.prototype.forEach.call(list.children, function (child) {
        if (child.classList.contains('price') && !child.hasAttribute('data-ebinoya-sale-date')) {
          child.style.display = on ? 'none' : '';
        }
      });
    });
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; apply(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
  // フィルター・無限スクロール・クイックビュー等でカードが後から差し込まれた場合に追従
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  // 開きっぱなしのタブが0時をまたいだ場合に追従
  setInterval(apply, 60 * 1000);
})();
